import { CheckIcon } from '@/shared/ui/icons/CheckIcon'
import type { OrgNewDraft } from '@/public/features/org/types'
import { ORG_LAST_STEP, TOTAL_STEPS } from '@/public/features/org/constants/orgNew'
import { buttonStyles } from '@/shared/ui/Button'
import { ROW_CLASS } from '@/shared/ui/buttonRow'

export function SectionHead({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="m-0 text-body-lg leading-[1.4] font-medium text-black-600">{title}</p>
      <p className="m-0 text-body-sm font-medium text-black-300">{desc}</p>
    </div>
  )
}
export function StepHeadLarge({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="flex flex-col gap-[5px]">
      <p className="m-0 text-headline-xs font-normal tracking-[-0.1px] text-black-800">{title}</p>
      <p className="m-0 text-body-md font-normal text-black-200">{desc}</p>
    </div>
  )
}
export function Field({
  label,
  placeholder,
  value,
  onChange,
  type = 'text',
  inputMode,
  autoComplete,
  required = false,
  error = null,
  success = null,
  hint,
  onEnter,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (v: string) => void
  type?: string
  inputMode?: 'numeric' | 'tel' | 'text'
  autoComplete?: string
  required?: boolean
  error?: string | null
  success?: string | null
  hint?: string
  onEnter?: () => void
}) {
  const msgId = `${label}-msg`
  const hasError = error !== null

  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-start gap-1 text-caption font-medium text-black-800">
        {label}
        {required ? <span className="mt-0.5 h-1 w-1 shrink-0 rounded-full bg-violet-500" aria-hidden /> : null}
      </span>
      <input
        className={hasError ? 'w-full min-h-10 rounded-lg border bg-white-50 px-3 py-2.5 text-body-sm text-black-800 placeholder:text-black-100 border-[#f54550]' : 'w-full min-h-10 rounded-lg border bg-white-50 px-3 py-2.5 text-body-sm text-black-800 placeholder:text-black-100 border-[#dcdcdc]'}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={
          onEnter === undefined
            ? undefined
            : (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onEnter()
                }
              }
        }
        {...(inputMode !== undefined ? { inputMode } : {})}
        {...(autoComplete !== undefined ? { autoComplete } : {})}
        aria-invalid={hasError}
        {...(hasError ? { 'aria-describedby': msgId } : {})}
      />
      {hasError ? (
        <span id={msgId} className="text-caption text-[#f54550]" role="alert">
          {error}
        </span>
      ) : success !== null ? (
        <span className="flex items-center gap-1 text-caption text-[#27c241] [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0">
          <CheckIcon />
          <span>{success}</span>
        </span>
      ) : hint !== undefined ? (
        <span className="text-caption text-[#808080]">{hint}</span>
      ) : null}
    </label>
  )
}
export function Buttons({ draft, rowClass = ROW_CLASS }: { draft: OrgNewDraft; rowClass?: string }) {
  const isOrgLast = draft.step === ORG_LAST_STEP
  const isCardLast = draft.step === TOTAL_STEPS
  const isSubmitStep = isOrgLast || isCardLast

  return (
    <div className={rowClass}>
      <button
        type="button"
        className={buttonStyles('lg', 'assistive', 'ui-pressable w-[90px] shrink-0 px-[22px] py-3')}
        onClick={draft.goPrev}
      >
        이전
      </button>
      <button
        type="button"
        className={buttonStyles('lg', 'primary', 'ui-pressable min-w-0 flex-1 shrink-0 grow basis-0')}
        disabled={!draft.canGoNext || draft.isSubmitting}
        onClick={isSubmitStep ? draft.submit : draft.goNext}
      >
        {draft.isSubmitting
          ? '등록하는 중…'
          : isOrgLast
            ? '기업 등록하기'
            : isCardLast
              ? '해결카드 등록하기'
              : '다음'}
      </button>
    </div>
  )
}

export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}
